import {
  schMainPropertyType,
  schManufacturedHomeFoundation,
  schPositiveInt,
  schSolarPanel,
  schTermsOfSale,
  syntaxFNMA,
} from '@aloft/toolkit-shared';

import { z } from 'zod';

import { schIsoMonth } from '../market/types.ts';
import {
  schArea,
  schAreaWithUnit,
  schCraftsmanSFRQualityClasses,
  schFormPositiveInt,
  schPrice,
  schRequiredArea,
} from './types.ts';

const schAdditionalStructure = z.object({
  hasFeature: z.boolean().optional().default(true),
});

// Basement does not need quality rating because it inherits it from the main dwelling
const schBasement = schAdditionalStructure.extend({
  finishedArea: schArea,
  totalArea: schArea,
  fullBaths: schFormPositiveInt.optional(),
  halfBaths: schFormPositiveInt.optional(),
});

const schThreeTierQuality = z
  .string()
  .pipe(z.coerce.number().int().min(1).max(3))
  .transform((val) => +val);

const schSixTierQuality = z
  .string()
  .optional()
  .pipe(z.coerce.number().int().min(1).max(6))
  .transform((val) => +val);

const schCraftsmanQualityOptional = z
  .string()
  .optional()
  .pipe(z.coerce.number().min(1).max(6).multipleOf(0.1).optional())
  .transform((val) => (val ? +val : undefined));

const schParking = schAdditionalStructure.extend({
  area: schRequiredArea,
  spaces: schFormPositiveInt.optional(),
  quality: schSixTierQuality,
});

const schAreaAndQuality = schAdditionalStructure
  .extend({
    area: schRequiredArea,
    quality: schThreeTierQuality,
  })
  .or(z.undefined());

const schAtticFeature = z.object({
  hasFeature: z.boolean(),
  area: schRequiredArea,
  quality: schSixTierQuality,
});

const schPorchPatioDeck = schAdditionalStructure
  .extend({
    type: z.enum(['covered', 'screen', 'uncovered']),
    area: schRequiredArea,
    quality: schSixTierQuality,
  })
  .or(z.undefined());

const schADU = z
  .object({
    area: schRequiredArea,
    quality: schCraftsmanSFRQualityClasses,
    halfBaths: schFormPositiveInt.optional(),
    fullBaths: schFormPositiveInt.optional(),
    hasFeature: z.boolean().optional().default(true),
  })
  .or(z.undefined());

const schUADQualityAndCondition = z
  .string()
  .transform((str) => str.replace(/[CQ]/g, ''))
  .pipe(z.coerce.number().min(1).max(6))
  .or(z.union([z.literal(''), z.literal('C'), z.literal('Q')]).transform(() => undefined));

const schUadOptions = syntaxFNMA.schUadListingStatus
  .or(z.literal(''))
  .transform((x) => (x === '' ? undefined : x));

const schTermsOfSaleOptions = schTermsOfSale
  .or(z.literal(''))
  .transform((x) => (x === '' ? undefined : x));

const schOptionalIsoMo = schIsoMonth.or(z.literal('')).transform((x) => (x === '' ? undefined : x));

const schFireplace = schAdditionalStructure.extend({
  count: schFormPositiveInt,
  type: z.enum(['freestanding', 'zeroClearance', 'masonry']),
});

// TODO: Refactor this so it uses the Improvements type (and the form ultimately uses property data objects)
export const schObjectiveCharacteristics = z
  .object({
    id: z.string(),
    gla: schRequiredArea,
    numberOfUnits: schPositiveInt.optional(),
    garage: schParking.optional(),
    detachedGarage: schParking.optional(),
    carport: schParking.optional(),
    fullBaths: schFormPositiveInt.optional(),
    halfBaths: schFormPositiveInt.optional(),
    basement: schBasement.optional(),
    attic: schAtticFeature.optional(),
    fireplace: schFireplace.optional(),
    shed: schAreaAndQuality.optional(),
    workshop: schAreaAndQuality.optional(),
    barn: schAreaAndQuality.optional(),
    pool: schAreaAndQuality.optional(),
    spa: schAreaAndQuality.optional(),
    area: schAreaWithUnit.optional(),
    location: z.string().optional(),
    view: z.string().optional(),
    notes: z.string().optional(),
    price: schPrice.optional(),
    adu: schADU.optional(),
    porch: schPorchPatioDeck.optional(),
    patio: schPorchPatioDeck.optional(),
    deck: schPorchPatioDeck.optional(),
    manualCost: schPrice.optional(),
    manualCostDisplayName: z.string().optional(),
    craftsmanQuality: schCraftsmanQualityOptional,
    uadQuality: schUADQualityAndCondition.optional(),
    uadCondition: schUADQualityAndCondition.optional(),
    age: schFormPositiveInt.optional(),
    concessions: z.string().optional(),
    saleDate: schOptionalIsoMo,
    contractDate: schOptionalIsoMo,
    withdrawalDate: schOptionalIsoMo,
    expirationDate: schOptionalIsoMo,
    status: schUadOptions,
    termsOfSale: schTermsOfSaleOptions,
    foundation: schManufacturedHomeFoundation.optional(),
    originalListPrice: schPrice.optional(),
    daysOnMarket: schFormPositiveInt.optional(),
    propertyType: schMainPropertyType,
    solarPanel: schSolarPanel.optional(),
  })
  .refine(
    (data) => {
      if (data.id !== 'subject' && data.status === 'Contract' && !data.contractDate) {
        return false;
      }
      return true;
    },
    {
      message: 'Contract Date is required when Status is Contract.',
      path: ['contractDate'],
    },
  )
  .refine(
    (data) => {
      if (data.id !== 'subject' && data.status === 'Expired' && !data.expirationDate) {
        return false;
      }
      return true;
    },
    {
      message: 'Expiration Date is required when status is Expired.',
      path: ['expirationDate'],
    },
  )
  .refine(
    (data) => {
      if (data.id !== 'subject' && data.status === 'Withdrawn' && !data.withdrawalDate) {
        return false;
      }
      return true;
    },
    {
      message: 'Withdrawal Date is required when status is Withdrawn.',
      path: ['withdrawalDate'],
    },
  )
  .refine(
    (data) => {
      if (data.id !== 'subject' && data.status === 'SettledSale' && !data.saleDate) {
        return false;
      }
      return true;
    },
    {
      message: 'Sale Date is required when Status is SettledSale.',
      path: ['saleDate'],
    },
  )
  .refine(
    (data) => {
      const bathroomCount = (data.basement?.fullBaths ?? 0) + (data.basement?.halfBaths ?? 0);
      const basementArea =
        (data.basement?.totalArea.unitAmount ?? 0) + (data.basement?.finishedArea.unitAmount ?? 0);

      if (bathroomCount > 0 && basementArea === 0) {
        return false;
      }
      return true;
    },
    {
      message: 'Basement total area must be greater than 0 if bathrooms are present.',
      path: ['basement.totalArea'],
    },
  )
  .refine(
    (data) => {
      const basementArea = data.basement?.totalArea.unitAmount ?? 0;
      const basementFinishedArea = data.basement?.finishedArea.unitAmount ?? 0;

      if (basementFinishedArea > basementArea) {
        return false;
      }
      return true;
    },
    {
      message: 'Basement finished area must be less than or equal to total area.',
      path: ['basement.finishedArea'],
    },
  )
  .refine(
    (data) => {
      if (data.craftsmanQuality === undefined || data.craftsmanQuality === null) {
        return false;
      }
      return true;
    },
    {
      message: 'Cost Quality is required.',
      path: ['craftsmanQuality'],
    },
  );

export type ObjectiveCharacteristicsFormInput = z.input<typeof schObjectiveCharacteristics>;
export type ObjectiveCharacteristicsFormOutput = z.output<typeof schObjectiveCharacteristics>;
